import React, { useState, useEffect } from "react";
import { FaClock, FaSave, FaWallet } from "react-icons/fa";
import { Drawer } from "../../shared";
import { NumberInput } from "../../../Components/atoms";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";

export const PERIOD_LABELS = {
  daily: "Günlük",
  weekly: "Haftalık",
  monthly: "Aylık",
};

/** Kuruş → "₺12.50" */
export const formatTL = (kurus) => `₺${((Number(kurus) || 0) / 100).toFixed(2)}`;

/**
 * Çalışanın bu dönem kalan kotasını elle düzeltme (#362).
 * Dönem ve tutar yalnız çalışan şablonundan gelir — burada salt okunur;
 * değiştirmek için Çalışan Şablonları sekmesi kullanılır.
 */
const EmployeeQuotaDrawer = ({ open, onClose, employee: employeeProp, onSave, isLoading }) => {
  // Kapanış animasyonu boyunca çalışan bilgisi dondurulur.
  const { mounted, data: employee } = useDrawerTransition(open, employeeProp);
  const [periodicWalletRemaining, setPeriodicWalletRemaining] = useState(0);

  useEffect(() => {
    // Para alanları kuruş cinsinden tutulur; admin'e TL göster (/100).
    setPeriodicWalletRemaining(
      (employee?.quotaOption?.periodicWalletRemaining || 0) / 100
    );
  }, [employee]);

  const handleSubmit = () => {
    // TL girişini kuruşa çevirip sakla (tüm para alanları kuruş).
    onSave({
      userId: employee._id,
      periodicWalletRemaining: Math.max(
        0,
        Math.round((Number(periodicWalletRemaining) || 0) * 100)
      ),
    });
  };

  if (!mounted || !employee) return null;

  const template = employee.employeeTemplateInfo;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Kota Ayarları"
      subtitle={`${employee.name} ${employee.lastName}`}
      titleIcon={<FaClock className="w-4 h-4 text-indigo-600" />}
      width="sm"
    >
      <Drawer.Body className="p-4 space-y-4">
        {/* Şablon — dönem ve tutarın tek kaynağı */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <FaClock className="w-3.5 h-3.5 text-indigo-600" />
            <h3 className="text-sm font-Bold text-gray-800">Çalışan Şablonu</h3>
          </div>
          {template ? (
            <div className="space-y-1 text-xs text-gray-600">
              <p>
                <span className="font-SemiBold text-gray-800">{template.name}</span>
              </p>
              <p>
                {PERIOD_LABELS[template.periodType] || template.periodType} kota:{" "}
                <span className="font-SemiBold text-gray-800">{formatTL(template.periodQuota)}</span>
              </p>
              <p className="text-[11px] text-gray-400 pt-1">
                Dönem ve tutar Çalışan Şablonları sekmesinden değiştirilir.
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-400">Şablon bulunamadı</p>
          )}
        </div>

        {/* Bu dönem kalan kota */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <FaWallet className="w-3.5 h-3.5 text-emerald-600" />
            <h3 className="text-sm font-Bold text-gray-800">Bu Dönem Kalan Kota</h3>
          </div>
          <NumberInput
            label="Kalan Kota (₺)"
            value={periodicWalletRemaining}
            onChange={(e) => setPeriodicWalletRemaining(Number(e.target.value))}
            min={0}
          />
          <p className="text-[11px] text-gray-400 mt-2">
            Düzeltme bu dönem için geçerlidir; yeni dönemde kota şablon tutarına döner.
          </p>
        </div>
      </Drawer.Body>

      <Drawer.Footer className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            <FaSave className="w-3.5 h-3.5" />
            {isLoading ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
};

export default EmployeeQuotaDrawer;
